import { useState } from "react";
import { toast } from "sonner";
import { Lock, Link2 } from "lucide-react";
import { SlippageSaved } from "./SlippageSaved";
import { DealChat } from "./DealChat";
import { TrustPanel } from "./TrustPanel";
import { useTokens } from "../../lib/queries";
import { fmtAmt, fmtUsd, short, whyEscrow, STATUS, dealLink } from "../../lib/format";

const TABS = [["slippage", "Impact saved"], ["chat", "Negotiation"], ["trust", "Token trust"]];

export const DealDetail = ({ deal }) => {
  const [tab, setTab] = useState("slippage");
  const { data: tokens } = useTokens();
  const token = tokens?.find((t) => t.symbol === deal?.token_symbol);

  if (!deal) return (
    <section className="panel h-[300px] shrink-0 flex items-center justify-center text-sm text-fog px-6 text-center" data-testid="deal-detail-empty">
      Select a listing above to see its impact-saved analysis, negotiation thread and token trust signals.
    </section>
  );
  const s = STATUS[deal.status];
  const stock = deal.token?.stock || deal.stock;
  return (
    <section className="panel h-[340px] shrink-0 flex flex-col min-h-0" data-testid="deal-detail-container">
      <div className="panel-head max-lg:h-auto max-lg:flex-wrap max-lg:gap-2 max-lg:py-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-heading font-semibold text-white">{deal.token_symbol}</span>
          <span className="tag border-violet/40 text-violet-300">/{stock}</span>
          <span className="mono text-xs text-fog">{deal.id}</span>
          <span className={`tag ${s.cls}`}>{s.label}</span>
          {deal.private && (
            <button
              onClick={() => { const l = dealLink(deal.id); navigator.clipboard?.writeText(l).then(() => toast.success("Invite link copied")).catch(() => {}); }}
              data-testid="detail-private-link-btn"
              title="Copy the invite link for this private deal"
              className="tag border-violet/50 text-violet-300 bg-violet/10 flex items-center gap-1 hover:border-violet transition-colors"
            >
              <Lock size={10} /> PRIVATE <Link2 size={10} className="ml-0.5" />
            </button>
          )}
          <span className="mono text-[11px] text-fog hidden lg:inline">{fmtAmt(deal.token_amount)} tokens · seller {short(deal.seller_address)} · buyer {short(deal.buyer_address)}</span>
        </div>
        <div className="flex bg-ink rounded border border-line2 p-0.5">
          {TABS.map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`detail-tab-${id}`}
              className={`h-6 px-2.5 rounded-sm text-[11px] font-medium transition-colors ${tab === id ? "bg-violet text-white" : "text-fog hover:text-white"}`}>
              {l}{id === "chat" && deal.message_count > 0 && <span className="ml-1 text-slate-500">{deal.message_count}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {tab === "slippage" && (
          <div className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            <SlippageSaved s={deal.slippage} symbol={deal.token_symbol} amount={deal.token_amount} />
            <div className="text-xs text-slate-400 leading-relaxed space-y-2">
              <p><span className="text-white font-medium">Why this trade would move the market.</span> {whyEscrow(stock)}</p>
              <p>Selling {fmtAmt(deal.token_amount)} {deal.token_symbol} into ~{fmtUsd(deal.token.pool_depth_usd)} of pool depth walks the curve down and pushes the pool price off {stock}'s real quote — which arbitrage then propagates. Cross settles the whole block at one flat price, so the pool never sees it.</p>
              {deal.note && <p className="mono text-[11px] text-slate-300 border-l-2 border-violet pl-2">"{deal.note}"</p>}
            </div>
          </div>
        )}
        {tab === "chat" && <DealChat deal={deal} />}
        {tab === "trust" && <TrustPanel token={token} />}
      </div>
    </section>
  );
};
